"use client";
import clsx from "clsx";
import { type HTMLInputTypeAttribute, type InputHTMLAttributes, type ReactNode, useState } from "react";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";
import { EyeIcon } from "../icons/EyeIcon";
import { EyeSlashIcon } from "../icons/EyeSlashIcon";

type Props<T extends FieldValues> = {
  label: string;
  name: Path<T>;
  control: Control<T>;
  className?: string;
  inputClassName?: string;
  icon?: ReactNode;
  inverse?: boolean;
} & InputHTMLAttributes<HTMLInputElement>;
export const Input = <T extends FieldValues>({
  id,
  label,
  name,
  control,
  className,
  inputClassName,
  type,
  icon,
  inverse = false,
  ...props
}: Props<T>) => {
  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({ name, control });
  const [currentType, setCurrentType] = useState<HTMLInputTypeAttribute>(type || "text");

  const handleSwitch = () => {
    if (currentType === "password") setCurrentType("text");
    if (currentType === "text") setCurrentType("password");
  };

  return (
    <div className={clsx("flex flex-col gap-1", className)}>
      {label && (
        <label htmlFor={id} className="text-xs/[14px] pl-2">
          {label}
        </label>
      )}
      <div className="relative group">
        <input
          id={id}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          type={currentType}
          className={clsx(
            "w-full h-12 transition-colors group-hover:bg-[#FDFBF7] text-tx-1 focus:bg-white focus:border-tx-2 outline-none rounded-lg border px-4 text-base/5 font-normal placeholder:text-tx-2 placeholder:font-normal placeholder:text-base/5",
            inputClassName,
            {
              "border-no bg-no/[0.02]": error,
              "border-bg-2": !error,
              "bg-bg-1": !error && !inverse,
              "bg-white": !error && inverse,
              "pr-12": type === "password" || icon,
            },
          )}
          {...props}
        />
        {type === "password" && (
          <div
            className="w-6 h-6 select-none absolute top-[14px] right-3 grid place-items-center cursor-pointer"
            onClick={handleSwitch}
          >
            {currentType === "password" ? <EyeIcon /> : <EyeSlashIcon />}
          </div>
        )}
        {icon && <div className="absolute top-[18px] right-4">{icon}</div>}
      </div>
      {error?.message && <p className="text-no text-xs/[14px] pl-2">{error.message}</p>}
    </div>
  );
};
